import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { updateSearchParams } from '../common/reducers/updateSearchParams.reducers';

import { INITIAL_DRAFT_DOCUMENTS_STATE } from './draft.state';
import { setOverrides, updateOverrides } from './reducers/desktop.settingsOverrides.reducers';
import {
  decrementDraftCounter,
  fetchDraftCounterFailure,
  fetchDraftCounterSuccess,
  incrementDraftCounter,
} from './reducers/draft.counters.reducers';

import {
  resetCurrentIdentity,
  setCurrentIdentity,
} from './reducers/draft.selectedIdentity.reducers';

const slice = createSlice({
  name: 'draftDocuments',
  initialState: INITIAL_DRAFT_DOCUMENTS_STATE,
  reducers: {
    setOverrides: (state, action) => setOverrides(state, action),
    updateOverrides: (state, action) => updateOverrides(state, action),

    updateSearchParams,

    // `force` bypasses the TTL gate in getTotalDraftSaga: a write whose arithmetic only the server
    // knows (a split) has no other way to correct the total, unlike a delta the client can patch.
    fetchDraftCounters: (state, _action: PayloadAction<{ force?: boolean } | undefined>) => state,
    fetchDraftCounterSuccess,
    fetchDraftCounterFailure,
    incrementDraftCounter,
    decrementDraftCounter,
    setCurrentIdentity,
    resetCurrentIdentity,
  },
});

export const documentDraftReducer = slice.reducer;

export const DraftDocumentsActions = slice.actions;

// The only way to ask for a counter resync — never dispatch DraftDocumentsActions.fetchDraftCounters
// directly. That action creator takes no payload, so a caller forgetting the `()` (dispatching the
// bare function reference) type-checks and silently no-ops under redux-thunk. This wrapper can't be
// called wrong.
//
// `force: true` bypasses the 10-minute TTL — for a write whose delta the client cannot compute (a
// split), a stale-but-not-expired counter would otherwise ignore the request to resync silently.
export const refreshDraftCounters = (options?: { force?: boolean }) =>
  DraftDocumentsActions.fetchDraftCounters(options);
